import React, { useState, useEffect, useCallback } from 'react';
import { getTournamentsApi, updateTournamentApi, cancelTournamentApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Trophy, Trash2, Plus, X, ChevronLeft, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function TournamentManager() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editCharges, setEditCharges] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await getTournamentsApi();
      setTournaments(res.data);
    } catch { toast.error('Failed to load tournaments'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id, name) => {
    if (!window.confirm(`Cancel "${name}" and release all courts?`)) return;
    try {
      await cancelTournamentApi(id);
      toast.success('Tournament cancelled');
      load();
    } catch { toast.error('Failed to cancel'); }
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await updateTournamentApi(id, {
        additional_charges: editCharges.filter(c => c.name && parseFloat(c.amount) > 0)
      });
      toast.success('Updated');
      setEditingId(null);
      load();
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const formatHour = (h) => {
    if (h === 0 || h === 24) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#313638] text-white px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-lg p-1.5 transition" data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <Trophy className="w-5 h-5 text-amber-400" />
        <h1 className="font-bold text-lg">Tournament Manager</h1>
        <span className="text-xs bg-white/20 px-2.5 py-0.5 rounded-full ml-auto">{tournaments.length} tournament{tournaments.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="max-w-3xl mx-auto p-6 space-y-4">
        {tournaments.length === 0 && (
          <div className="text-center text-gray-400 py-16">No active tournaments</div>
        )}

        {tournaments.map(t => {
          const courtCost = t.court_cost || 0;
          const addlTotal = (t.additional_charges || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
          const total = courtCost + addlTotal;
          const isEditing = editingId === t.id;

          return (
            <div key={t.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid={`tournament-${t.id}`}>
              <div className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900">{t.name}</h3>
                    <p className="text-sm text-gray-500">{t.organizer_name}{t.organizer_phone ? ` · ${t.organizer_phone}` : ''}</p>
                  </div>
                  <span className="text-lg font-bold text-gray-900">₱{total.toLocaleString()}</span>
                </div>

                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="bg-gray-100 px-2.5 py-1 rounded-full">{t.start_date}{t.end_date !== t.start_date ? ` → ${t.end_date}` : ''}</span>
                  <span className="bg-gray-100 px-2.5 py-1 rounded-full">{formatHour(t.start_hour)} – {formatHour(t.end_hour)}</span>
                  <span className="bg-gray-100 px-2.5 py-1 rounded-full">{t.court_names?.join(', ')}</span>
                  <span className="bg-gray-100 px-2.5 py-1 rounded-full">{t.num_days} day{t.num_days > 1 ? 's' : ''}</span>
                </div>

                <div className="text-xs text-gray-500 flex gap-4">
                  <span>Court rental: ₱{courtCost.toLocaleString()}</span>
                  <span>Additional: ₱{addlTotal.toLocaleString()}</span>
                </div>

                {/* Additional Charges Detail / Edit */}
                {isEditing ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-amber-900">Additional Charges</span>
                      <button type="button" onClick={() => setEditCharges(prev => [...prev, { name: '', amount: '' }])} className="text-xs text-amber-700 font-medium flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Add
                      </button>
                    </div>
                    {editCharges.map((c, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input value={c.name} onChange={(e) => { const u = [...editCharges]; u[i] = { ...u[i], name: e.target.value }; setEditCharges(u); }} placeholder="Charge name" className="rounded-lg flex-1 h-8 text-sm" />
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">₱</span>
                          <Input type="number" value={c.amount} onChange={(e) => { const u = [...editCharges]; u[i] = { ...u[i], amount: e.target.value }; setEditCharges(u); }} placeholder="0" className="rounded-lg w-28 h-8 text-sm pl-6" />
                        </div>
                        <button type="button" onClick={() => setEditCharges(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-blocks"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <Button onClick={() => handleSave(t.id)} disabled={saving} className="rounded-xl text-white text-sm h-8 px-4" style={{ backgroundColor: '#D97706' }}>
                        <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button variant="outline" onClick={() => setEditingId(null)} className="rounded-xl text-sm h-8 px-4">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  (t.additional_charges || []).length > 0 && (
                    <div className="text-xs text-gray-500 space-y-0.5">
                      {t.additional_charges.map((c, i) => (
                        <div key={i} className="flex justify-between"><span>{c.name}</span><span>₱{parseFloat(c.amount).toLocaleString()}</span></div>
                      ))}
                    </div>
                  )
                )}
              </div>

              <div className="border-t border-gray-100 px-5 py-3 flex justify-end gap-2">
                {!isEditing && (
                  <Button variant="outline" size="sm" className="rounded-xl text-xs h-8" onClick={() => { setEditingId(t.id); setEditCharges(t.additional_charges?.length ? [...t.additional_charges] : []); }}>
                    Edit Charges
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="rounded-xl text-xs h-8 text-blocks hover:text-blocks-hover hover:bg-blocks-tint" onClick={() => handleCancel(t.id, t.name)}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel Tournament
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
